import type { NuxtApp } from '#app'
import {
  type Attributes,
  type BasketItem,
  type BaskteItemDisplayDataItem,
  type CentAmount,
  getAttributeValue,
  getAttributeValueTuples,
  getFlattenedAdvancedAttribute,
  type Price,
  type Value,
  type Variant,
} from '@scayle/storefront-nuxt'
import { toValue } from 'vue'
import { getSortedAttributeValueTuples } from '~/utils/attribute'

export const SUBSCRIPTION_ELIGIBILITY_ATTRIBUTE_NAME = 'subscriptionEligibility'
export const SUBSCRIPTION_ELIGIBILITY_PRODUCT_ATTRIBUTE_NAME =
  'subscriptionEligibilityProduct'
export const SUBSCRIPTION_INTERVALS_ATTRIBUTE_NAME =
  'subscriptionAvailableIntervals'
export const SUBSCRIPTION_TERM = 'subscriptionTerm'
export const SUBSCRIPTION_CANCELLATION_POLICY = 'subscriptionCancellationPolicy'
export const SUBSCRIPTION_DISCOUNT_TEXT = 'subscriptionDiscountText'
export const SUBSCRIPTION_DISCOUNT_VALUE = 'subscriptionDiscountValue'
export const SUBSCRIPTION_DISCOUNT_UNIT = 'subscriptionDiscountUnit'
export const SUBSCRIPTION_SUBSEQUENT_DISCOUNT = 'subscriptionSubsequentDiscount'

export type PreferredDeliveryDate = {
  day: number
  label: string
}

export type SubscriptionChangeData = {
  subsequentPrice?: MaybeRefOrGetter<number | undefined>
  pricePromotionKey?: MaybeRefOrGetter<string>
  subscriptionTerm: MaybeRefOrGetter<Value | undefined>
  subscriptionCancellationPolicy: MaybeRefOrGetter<Value | undefined>
  selectedInterval: MaybeRefOrGetter<Value | undefined>
  selectedPreferredDeliveryDate: MaybeRefOrGetter<
    PreferredDeliveryDate | undefined
  >
}
export type SubscriptionDefinition = {
  subscriptionInterval?: string
  subscriptionDeliveryDate?: number
  subscriptionTerm?: string
  subscriptionCancellationPolicy?: string
}

export type BasketItemSubscriptionAttributes = {
  customData: {
    subscriptionDefinition: SubscriptionDefinition
    pricePromotionKey?: string
  }
  displayData: {
    'attribute-1': {
      key: string
      label: string
      value: string
    }
  }
}

export type SubscriptionDiscount = {
  value: number
  unit: string
  text?: string
}

export const getSubscriptionDeliveryDates =
  (): Array<PreferredDeliveryDate> => {
    return Array.from({ length: 30 }, (_, i) => ({
      day: i + 1,
      label: 'day_of_month_selection_caption',
    }))
  }

export const getSubsequentDiscountForVariant = (
  variant?: MaybeRefOrGetter<Variant | undefined>,
) => {
  const entry = toValue(variant)

  if (!entry) {
    return undefined
  }

  // Values can either be stored in 3 attributes
  const text = getFirstAttributeLabel(
    entry?.attributes ?? {},
    SUBSCRIPTION_DISCOUNT_TEXT,
  )
  const unit = getFirstAttributeLabel(
    entry?.attributes ?? {},
    SUBSCRIPTION_DISCOUNT_UNIT,
  )
  const value = getFirstAttributeLabel(
    entry?.attributes ?? {},
    SUBSCRIPTION_DISCOUNT_VALUE,
  )

  // we need to make sure groupSet & fieldSet are both available
  // getFlattenedAdvancedAttribute will break otherwise & at least groupSet seems to be missing
  // in advancedAttributes from orderItem variant
  const subsequentDiscountValues =
    entry?.advancedAttributes?.[SUBSCRIPTION_SUBSEQUENT_DISCOUNT]?.values?.map(
      (value) => ({
        fieldSet: value?.fieldSet || [],
        groupSet: value?.groupSet || [],
      }),
    ) ?? []

  // or as an advancedAttribute
  const [subsequentDiscount] = getFlattenedAdvancedAttribute<{
    subscriptionDiscountValue: string
    subscriptionDiscountUnit: string
    subscriptionDiscountText: string
  }>(subsequentDiscountValues)

  const discount: SubscriptionDiscount = {
    value: parseFloat(value || subsequentDiscount?.subscriptionDiscountValue),
    unit: unit || subsequentDiscount?.subscriptionDiscountUnit,
    text: text || subsequentDiscount?.subscriptionDiscountText,
  }

  if (discount.value && discount.unit && discount.text) {
    return discount
  }
}

export const calculateDiscountCentAmout = (
  value: number | CentAmount,
  discount: SubscriptionDiscount,
) => {
  if (discount.unit === '%') {
    return Math.floor(value * ((100 - discount.value) / 100))
  }

  console.error(`Discount unit "${discount.unit}" not implemented`)
  return value
}

export const computeSubscriptionDiscount = (
  originalPrice?: Price,
  newPrice?: Price,
): SubscriptionDiscount | undefined => {
  if (
    !!originalPrice?.withTax &&
    !!newPrice?.withTax &&
    originalPrice.withTax !== newPrice.withTax
  ) {
    return {
      value: Math.floor(100 - (100 / originalPrice.withTax) * newPrice.withTax),
      unit: '%',
    }
  }
}

// check if at least one variant of the product or the product itself is eligible for subscription
export const isProductSubscriptionEligible = (
  product?: ObjectWith<{
    variants?: Array<Variant | undefined>
    attributes?: Attributes
  }>,
) => {
  const variants = product?.variants?.filter(Boolean) ?? []

  const productHasSubscription =
    getAttributeValue(
      product?.attributes,
      SUBSCRIPTION_ELIGIBILITY_PRODUCT_ATTRIBUTE_NAME,
    ) === 'true'

  return (
    productHasSubscription ||
    (!!variants.length &&
      variants.some((variant) => {
        return (
          getAttributeValue(
            variant.attributes,
            SUBSCRIPTION_ELIGIBILITY_ATTRIBUTE_NAME,
          ) === 'true'
        )
      }))
  )
}

// check if the current variant fulfills the subscriptionEligibility condition
// and return all available intervals with a numeric sorting if so
export const getSubscriptionIntervals = (
  variant?: Variant,
  locale?: Intl.LocalesArgument,
) => {
  if (!variant) {
    return []
  }

  const attributeValue = getAttributeValue(
    variant.attributes,
    SUBSCRIPTION_ELIGIBILITY_ATTRIBUTE_NAME,
  )

  if (attributeValue !== 'true') {
    return []
  }

  return getSortedAttributeValueTuples(
    variant.attributes,
    SUBSCRIPTION_INTERVALS_ATTRIBUTE_NAME,
    locale,
    {
      ignorePunctuation: true,
      numeric: true,
    },
  )
}

export const getSubscriptionTerm = (
  variant?: MaybeRefOrGetter<Variant | undefined>,
) => {
  const entry = toValue(variant)
  if (!entry) {
    return
  }

  return getFirstAttributeValue(entry?.attributes, SUBSCRIPTION_TERM)
}

export const getSubscriptionCancellationPolicy = (
  variant?: MaybeRefOrGetter<Variant | undefined>,
) => {
  const entry = toValue(variant)
  if (!entry) {
    return
  }

  return getFirstAttributeValue(
    entry?.attributes,
    SUBSCRIPTION_CANCELLATION_POLICY,
  )
}

export const getOrdinalSuffix = (locale: string, value?: number) => {
  if (!value) {
    return
  }
  const ordinalRules = new Intl.PluralRules(locale, { type: 'ordinal' })

  return ordinalRules.select(value)
}

export const hasSubscriptionDefined = (
  entry?: MaybeRefOrGetter<ObjectWith<{ customData: any }>>,
) =>
  Object.hasOwnProperty.call(
    toValue(entry)?.customData || {},
    'subscriptionDefinition',
  )

export const getSubscriptionDefinition = (
  entry?: MaybeRefOrGetter<ObjectWith<{ customData: any }>>,
) => {
  if (hasSubscriptionDefined(entry)) {
    return toValue(entry)?.customData
      ?.subscriptionDefinition as SubscriptionDefinition
  }
}

export const getSubscriptionCustomData = (data: SubscriptionChangeData) => ({
  subscriptionDefinition: {
    subscriptionInterval: toValue(data.selectedInterval)?.value,
    subscriptionDeliveryDate: toValue(data.selectedPreferredDeliveryDate)?.day,
    subscriptionTerm: toValue(data.subscriptionTerm)?.value,
    subscriptionCancellationPolicy: toValue(data.subscriptionCancellationPolicy)
      ?.value,
  },
  pricePromotionKey: toValue(data.pricePromotionKey),
  subscription: true,
})

export const getSubscriptionSettings = (
  data: SubscriptionChangeData,
  $i18n: NuxtApp['$i18n'],
): Array<{ label: string; value: string | undefined }> => {
  const ordinalSuffixKey = computed(() =>
    getOrdinalSuffix(
      $i18n.locale,
      toValue(data.selectedPreferredDeliveryDate)?.day,
    ),
  )

  const subsequentPrice = roundSubsequentPriceCentAmount(
    toValue(data.subsequentPrice),
    $i18n,
  )

  return [
    subsequentPrice
      ? {
          label: $i18n.t('subscription.subsequent_price'),
          value: toCurrency(subsequentPrice),
        }
      : undefined,
    {
      label: $i18n.t('subscription.interval'),
      value: toValue(data.selectedInterval)?.label,
    },
    {
      label: $i18n.t('subscription.follow_up_delivery'),
      value: `${$i18n.t('subscription.day_of_month_selection_caption', {
        dayOfMonth:
          toValue(data.selectedPreferredDeliveryDate)?.day +
          $i18n.t(`global.ordinal_suffixes.${ordinalSuffixKey.value}`),
      })}`,
    },
    {
      label: $i18n.t('subscription.term'),
      value: toValue(data.subscriptionTerm)?.label,
    },
    toValue(data.subscriptionCancellationPolicy)?.label
      ? {
          label: $i18n.t('subscription.cancellation_policy'),
          value: toValue(data.subscriptionCancellationPolicy)?.label,
        }
      : undefined,
  ].filter(Boolean)
}

export const getSubscriptionDisplayDataItem = (
  data: SubscriptionChangeData,
  $i18n: NuxtApp['$i18n'],
): BaskteItemDisplayDataItem => {
  const subscriptionSettings = getSubscriptionSettings(data, $i18n)
  const value = subscriptionSettings
    .map(({ label, value }) => `${label}: ${value}`)
    .join(' | ')

  return {
    key: 'subscriptionDefinition',
    label: $i18n.t('subscription.title'),
    value,
  }
}

export const getOrderItemSubscriptionSettings = (
  item: MaybeRefOrGetter<FimBasketItem | undefined>,
  variants: MaybeRefOrGetter<
    | ObjectWith<{ price: Variant['price']; id: Variant['id'] }>[]
    | undefined
    | null
  >,
  $i18n: NuxtApp['$i18n'],
) => {
  const orderItem = toValue(item)
  const variant = orderItem?.variant
  const variantWithPrice = toValue(variants)?.find(
    (item) => item.id === variant?.id,
  )
  const subscriptionDefinition = orderItem?.customData?.subscriptionDefinition

  if (!variant || !subscriptionDefinition) {
    return
  }
  const intervals = getSubscriptionIntervals(variant)
  const selectedInterval = intervals.find(
    (interval) =>
      interval.value === subscriptionDefinition.subscriptionInterval,
  )

  const subsequent = getSubsequentDiscountForVariant(variant)
  const price = variant.price?.withTax ?? variantWithPrice?.price?.withTax

  const subsequentPrice =
    (!!price &&
      !!subsequent &&
      // we need to manually calculate subsequent discounts
      calculateDiscountCentAmout(price, subsequent)) ||
    price

  const subscriptionDeliveryDates = getSubscriptionDeliveryDates()
  const selectedPreferredDeliveryDate = subscriptionDeliveryDates.find(
    (entry) => entry.day === subscriptionDefinition.subscriptionDeliveryDate,
  )

  const subscriptionTerm = getSubscriptionTerm(variant)
  const subscriptionCancellationPolicy =
    getSubscriptionCancellationPolicy(variant)

  return getSubscriptionSettings(
    {
      subsequentPrice: roundSubsequentPriceCentAmount(subsequentPrice, $i18n),
      subscriptionTerm,
      subscriptionCancellationPolicy,
      selectedInterval,
      selectedPreferredDeliveryDate,
    },
    $i18n,
  )
}

// This check is for rejecting adding variant in case of:
// 1) try to add subscription variant (priorItemToAdd) but the variant (subscription or not doesn't matter) is already in the basket
// 2) try to add non-subscription variant but the variant is already in the basket, and it's a subscription variant
export const isSubscriptionAlreadyInBasket = (
  isSubscriptionVariant: boolean,
  variantId: number,
  basketItems?: BasketItem[],
) => {
  const variantAlreadyInBasket = basketItems?.find(
    (basketItem) => basketItem.variant.id === variantId,
  )

  const hasVariantInBasketSubscriptionDefined = hasSubscriptionDefined(
    variantAlreadyInBasket,
  )

  return (
    (isSubscriptionVariant && variantAlreadyInBasket) ||
    (!isSubscriptionVariant &&
      variantAlreadyInBasket &&
      hasVariantInBasketSubscriptionDefined)
  )
}

/**
 * Implement rounding logic for subsequent price
 * DE: No rounding
 * AT: No rounding
 * CH: Rounding the decimals to the next lowest tens decimal (e.g. “29,90”; “29,80”; “29,70”; “29,60” etc.)
 * PL: Rounding the decimals to the next lowest tens decimal (e.g. “29,90”; “29,80”; “29,70”; “29,60” etc.)
 * CZ: Rounding to the next lowest integer (e.g. “29,00”, “300,00“)
 *
 * https://fielmann.atlassian.net/browse/CPP-4130
 * @param price
 * @param subsequent
 * @param $i18n
 */
export const roundSubsequentPriceCentAmount = (
  price: CentAmount | number | undefined,
  $i18n: NuxtApp['$i18n'],
) => {
  if (!price) {
    return undefined
  }
  const country = getIsoCountry($i18n.locale)

  switch (country) {
    case 'DE':
    case 'AT':
      return price
    case 'CH':
    case 'PL':
      return Math.floor(price / 10) * 10
    case 'CZ':
      return Math.floor(price / 100) * 100
    default:
      return price
  }
}

export const getPreferredDeliveryDate = (
  preferredDeliveryDates: Array<PreferredDeliveryDate>,
  day: number = new Date().getDate(),
): PreferredDeliveryDate | undefined => {
  const days = preferredDeliveryDates.map((deliveryDate) => deliveryDate.day)

  // Find the next available day by sorting the array of days by the absolute difference from the current day.
  // This ensures that we always use the most suitable day, even if the preferred delivery dates are not numbered sequentially.
  // Or the passed day is out of range (<1 || >30)
  const [nextAvailableDay] = days.sort((a, b) => {
    return Math.abs(day - a) - Math.abs(day - b) || a - b
  })

  const index = preferredDeliveryDates.findIndex(
    (deliveryDate) => deliveryDate.day === nextAvailableDay,
  )
  return index !== -1
    ? preferredDeliveryDates[index]
    : getSubscriptionDeliveryDates()?.[0]
}
